import type {
  ManageFirmOverviewResponse,
  ManageFirmOverviewServiceKey,
  ManageFirmOverviewSummaryRow,
} from "../../interfaces/models/IManageFirmOverview";
import TableHorizontalScrollContainer from "../../components/Organismos/Table/TableHorizontalScrollContainer";
import {
  MANAGE_FIRM_STATS_SECTION_TITLE_CLASS,
  MANAGE_FIRM_WSG_OVERVIEW_METRIC_CELL_CLASS,
} from "./manageFirmStatsPanelStyles";

function formatCount(value: number | null | undefined): string {
  if (value == null) {
    return "-";
  }
  return value.toLocaleString("en-US");
}

function isClickableServiceRow(row: ManageFirmOverviewSummaryRow): row is ManageFirmOverviewSummaryRow & {
  key: ManageFirmOverviewServiceKey;
} {
  return row.key !== "total";
}

type ColumnHeaderConfig = {
  icon: (props: { className?: string }) => JSX.Element;
  lines: [string, string];
};

const COLUMN_HEADERS: ColumnHeaderConfig[] = [
  {
    icon: UniqueFirmsIcon,
    lines: ["Unique Firms/", "No Multiple Jurisdictions"],
  },
  {
    icon: PrimaryJurisdictionsIcon,
    lines: ["Primary Jurisdictions", "Representation"],
  },
  {
    icon: SecondaryJurisdictionsIcon,
    lines: ["Secondary Jurisdictions", "Representation"],
  },
  {
    icon: ProfessionalsIcon,
    lines: ["Professionals", ""],
  },
];

function ColumnHeaderCard({
  icon: Icon,
  lines,
}: ColumnHeaderConfig): JSX.Element {
  return (
    <div className="flex min-h-[3.75rem] items-center gap-2 rounded-md bg-gray-25 px-3 py-2">
      <Icon className="h-6 w-6 shrink-0 text-Default" />
      <span className="text-left text-xs font-medium leading-tight text-gray-font">
        {lines[0]}
        {lines[1] ? (
          <>
            <br />
            {lines[1]}
          </>
        ) : null}
      </span>
    </div>
  );
}

type ManageFirmWsgOverviewPanelProps = {
  data: ManageFirmOverviewResponse | null;
  loading?: boolean;
  onServiceTypeClick?: (serviceType: ManageFirmOverviewServiceKey) => void;
};

export default function ManageFirmWsgOverviewPanel({
  data,
  loading = false,
  onServiceTypeClick,
}: ManageFirmWsgOverviewPanelProps): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-gray-200" />
          <div className="h-32 flex-1 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!data?.rows?.length) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-gray-font">WSG Overview is unavailable.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className={`${MANAGE_FIRM_STATS_SECTION_TITLE_CLASS} mb-4`}>
        WSG Overview
      </h3>

      <TableHorizontalScrollContainer
        className="flex-1 min-h-0"
        measureKey={data.rows.length}
      >
        <table className="min-w-full w-max border-collapse text-sm text-gray-font [&_tbody_td:first-child]:text-sm">
          <thead>
            <tr>
              <th className="min-w-[14rem] px-2 pb-3 align-bottom" />
              {COLUMN_HEADERS.map((header) => (
                <th
                  key={header.lines[0]}
                  className="min-w-[9.5rem] px-2 pb-3 align-bottom"
                >
                  <ColumnHeaderCard {...header} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const clickable = isClickableServiceRow(row);
              return (
                <tr
                  key={row.key}
                  className="border-t border-gray-100"
                >
                  <td className="px-2 py-3.5">
                    {clickable ? (
                      <button
                        type="button"
                        className="text-left font-medium text-Default hover:underline"
                        onClick={() => onServiceTypeClick?.(row.key)}
                      >
                        {row.label}
                      </button>
                    ) : (
                      <span className="font-semibold text-gray-font">
                        {row.label}
                      </span>
                    )}
                  </td>
                  <td
                    className={`px-2 py-3 ${MANAGE_FIRM_WSG_OVERVIEW_METRIC_CELL_CLASS}`}
                  >
                    {formatCount(row.uniqueFirmsNoMultipleJurisdictions)}
                  </td>
                  <td
                    className={`px-2 py-3 ${MANAGE_FIRM_WSG_OVERVIEW_METRIC_CELL_CLASS}`}
                  >
                    {formatCount(row.primaryJurisdictionsRepresentation)}
                  </td>
                  <td
                    className={`px-2 py-3 ${MANAGE_FIRM_WSG_OVERVIEW_METRIC_CELL_CLASS}`}
                  >
                    {formatCount(row.secondaryJurisdictionsRepresentation)}
                  </td>
                  <td
                    className={`px-2 py-3 ${MANAGE_FIRM_WSG_OVERVIEW_METRIC_CELL_CLASS}`}
                  >
                    {formatCount(row.professionals)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableHorizontalScrollContainer>

      <p className="mt-3 text-xs italic text-gray-500">*M: Members</p>
    </div>
  );
}

function UniqueFirmsIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 20V9l8-5 8 5v11H4z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M9 20v-5h6v5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M9 11h2M13 11h2M9 14h2M13 14h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PrimaryJurisdictionsIcon({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 12h17M12 3.5c2.5 2.8 3.8 5.6 3.8 8.5S14.5 17.7 12 20.5M12 3.5C9.5 6.3 8.2 9.1 8.2 12s1.3 5.7 3.8 8.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SecondaryJurisdictionsIcon({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4 10h12M10 3c2 2.2 3 4.4 3 7s-1 4.8-3 7M10 3c-2 2.2-3 4.4-3 7s1 4.8 3 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M16.5 16.5L21 21"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="17.5" cy="17.5" r="2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ProfessionalsIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="4"
        y="5"
        width="16"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8.5 16.5c.8-1.6 2-2.5 3.5-2.5s2.7.9 3.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
